import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Plus,
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  ChevronRight,
  Tag,
  Trash2,
  Loader2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import ticketService from '../services/ticketService';
import { Modal, Select } from '../components';

const STATUS_OPTIONS = [
  { value: 'NEW', label: 'New' },
  { value: 'IN_REVIEW', label: 'In Review' },
  { value: 'WAITING', label: 'Waiting' },
  { value: 'ESCALATED', label: 'Escalated' },
  { value: 'RESOLVED', label: 'Resolved' },
];
const statusLabel = (s) => STATUS_OPTIONS.find((o) => o.value === s)?.label || s;

const CATEGORY_OPTIONS = ['General', 'Access', 'Billing and Plan', 'Dashboard Issue', 'App Issue', 'Organisation Setup', 'Other'];

const STAT_CARDS = [
  { key: 'NEW', title: 'NEW', subtitle: 'Not started', color: 'blue', icon: MessageSquare },
  { key: 'IN_REVIEW', title: 'IN REVIEW', subtitle: 'Being checked', color: 'orange', icon: Clock },
  { key: 'WAITING', title: 'WAITING', subtitle: 'Provider reply', color: 'indigo', icon: MessageSquare },
  { key: 'ESCALATED', title: 'ESCALATED', subtitle: 'Developer queue', color: 'red', icon: AlertCircle },
  { key: 'RESOLVED', title: 'RESOLVED', subtitle: 'Completed', color: 'green', icon: CheckCircle2 },
  { key: 'TOTAL', title: 'TOTAL', subtitle: 'All tickets', color: 'purple', icon: HelpCircle },
];

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'HIGH': return 'bg-red-50 text-red-600 border-red-100';
    case 'MEDIUM': return 'bg-orange-50 text-orange-600 border-orange-100';
    case 'LOW': return 'bg-blue-50 text-blue-600 border-blue-100';
    default: return 'bg-gray-50 text-gray-600 border-gray-100';
  }
};

const getStatusBorder = (color) => ({
  blue: 'border-l-blue-500',
  orange: 'border-l-orange-500',
  indigo: 'border-l-indigo-500',
  red: 'border-l-red-500',
  green: 'border-l-green-500',
  purple: 'border-l-purple-500',
}[color] || 'border-l-gray-500');

const formatTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const Support = () => {
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ NEW: 0, IN_REVIEW: 0, WAITING: 0, ESCALATED: 0, RESOLVED: 0, TOTAL: 0 });
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'General', priority: 'MEDIUM' });

  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ticketService.list({ status: selectedStatus || undefined, search: searchTerm.trim() || undefined });
      if (res.success) {
        setTickets(res.data || []);
        if (res.stats) setStats(res.stats);
      }
    } catch (err) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, searchTerm]);

  // Fetch on mount and whenever the status filter changes
  useEffect(() => { fetchTickets(); /* eslint-disable-next-line */ }, [selectedStatus]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      setSaving(true);
      const res = await ticketService.create({
        title: form.title.trim(),
        description: form.description.trim() || null,
        category: form.category,
        priority: form.priority,
      });
      if (res.success) {
        toast.success('Ticket created');
        setCreateOpen(false);
        setForm({ title: '', description: '', category: 'General', priority: 'MEDIUM' });
        fetchTickets();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (ticket, status) => {
    setUpdatingId(ticket.id);
    try {
      const res = await ticketService.update(ticket.id, { status });
      if (res.success) {
        setTickets((prev) => prev.map((t) => (t.id === ticket.id ? res.data : t)));
        toast.success('Status updated');
        fetchTickets();
      }
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (ticket) => {
    if (!window.confirm('Delete this ticket?')) return;
    setDeletingId(ticket.id);
    try {
      await ticketService.remove(ticket.id);
      toast.success('Ticket deleted');
      setTickets((prev) => prev.filter((t) => t.id !== ticket.id));
      fetchTickets();
    } catch (err) {
      toast.error('Failed to delete ticket');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6 pb-10 animate-in fade-in duration-500 font-sans text-[#1E293B] dark:text-gray-100">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-[26px] font-bold tracking-tight">Support</h1>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-1">
            {admin
              ? 'Manage all provider tickets, access issues, bugs, and escalations'
              : 'Raise a support request and track your own tickets'}
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#2563EB] text-white text-[13px] font-bold rounded-lg hover:bg-blue-700 transition-all shadow-md"
        >
          <Plus className="w-4 h-4" />
          Create Ticket
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white dark:bg-[#262b33] p-4 rounded-xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <input
            type="text"
            placeholder="Search by ticket, organisation, category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchTickets()}
            className="w-full pl-10 pr-4 py-2.5 bg-[#F8FAFC] dark:bg-[#2c313a] border border-[#E2E8F0] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]/10 focus:border-[#2563EB] text-[14px] transition-all"
          />
        </div>
        <div className="flex gap-2">
          <Select
            value={selectedStatus}
            onChange={(v) => setSelectedStatus(v)}
            options={[{ value: '', label: 'All statuses' }, ...STATUS_OPTIONS]}
            className="w-[160px]"
          />
          <button
            onClick={fetchTickets}
            className="px-6 py-2.5 bg-[#2563EB] text-white text-[13px] font-bold rounded-lg hover:bg-blue-700 transition-all shadow-sm"
          >
            Search
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {STAT_CARDS.map((card) => (
          <div
            key={card.key}
            className={`bg-white dark:bg-[#262b33] p-4 rounded-xl border-l-4 shadow-sm border border-[#E2E8F0] dark:border-gray-800 hover:shadow-md transition-all group ${getStatusBorder(card.color)}`}
          >
            <div className="flex justify-between items-start mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#64748B] dark:text-gray-400">
                {card.title}
              </span>
              <card.icon className="w-4 h-4 text-[#94A3B8] group-hover:text-gray-600" />
            </div>
            <div className="text-[24px] font-bold">{stats[card.key] ?? 0}</div>
            <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">{card.subtitle}</div>
          </div>
        ))}
      </div>

      {/* Tickets Table */}
      <div className="bg-white dark:bg-[#262b33] rounded-xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#F8FAFC] dark:bg-[#2c313a] border-b border-[#E2E8F0] dark:border-gray-800">
                <th className="px-6 py-4 text-left text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Ticket</th>
                <th className="px-6 py-4 text-left text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">{admin ? 'Organization' : 'Status'}</th>
                <th className="px-6 py-4 text-left text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Category</th>
                <th className="px-6 py-4 text-left text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-4 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9] dark:divide-gray-800">
              {loading ? (
                <tr><td colSpan="5" className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></td></tr>
              ) : tickets.length === 0 ? (
                <tr><td colSpan="5" className="py-16 text-center text-[14px] text-[#64748B] dark:text-gray-400">
                  No tickets found. {admin ? 'No one has raised a ticket yet.' : 'Click “Create Ticket” to raise one.'}
                </td></tr>
              ) : tickets.map((ticket) => (
                <React.Fragment key={ticket.id}>
                  <tr
                    className={`hover:bg-[#F8FAFC] dark:hover:bg-[#2c313a] transition-colors cursor-pointer ${expandedRow === ticket.id ? 'bg-[#F8FAFC] dark:bg-[#2c313a]' : ''}`}
                    onClick={() => setExpandedRow(expandedRow === ticket.id ? null : ticket.id)}
                  >
                    <td className="px-6 py-5">
                      <div className="flex items-start gap-4">
                        <div className="p-2.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400 shrink-0">
                          <MessageSquare className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-[14px] font-bold leading-tight">{ticket.title}</div>
                          <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1 font-medium">{ticket.user}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      {admin ? (
                        <>
                          <div className="text-[14px] font-bold text-[#475569] dark:text-gray-300">{ticket.organization}</div>
                          <div className="text-[12px] text-[#94A3B8] mt-1">{statusLabel(ticket.status)}</div>
                        </>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold bg-[#F1F5F9] dark:bg-[#383e47] text-[#475569] dark:text-gray-300">
                          {statusLabel(ticket.status)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-5">
                      <div className="text-[14px] font-bold text-[#475569] dark:text-gray-300">{ticket.category}</div>
                      <div className="text-[12px] text-[#94A3B8] mt-1">{formatTime(ticket.created_at)}</div>
                    </td>
                    <td className="px-6 py-5">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-black border uppercase tracking-widest ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-right">
                      <button className={`p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-all ${expandedRow === ticket.id ? 'rotate-90 bg-gray-100 dark:bg-gray-700' : ''}`}>
                        <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Row */}
                  {expandedRow === ticket.id && (
                    <tr>
                      <td colSpan="5" className="px-8 py-6 bg-[#F8FAFC] dark:bg-[#2c313a] border-t border-b border-[#E2E8F0] dark:border-gray-800">
                        <div className="max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div className="space-y-4">
                            <h4 className="text-[13px] font-bold flex items-center gap-2">
                              <MessageSquare className="w-4 h-4 text-blue-500" />
                              Issue Description
                            </h4>
                            <div className="bg-white dark:bg-[#262b33] p-5 rounded-xl border border-[#E2E8F0] dark:border-gray-800 text-[14px] text-[#475569] dark:text-gray-300 leading-relaxed shadow-sm min-h-[60px]">
                              {ticket.description || 'No additional details provided.'}
                            </div>
                            {ticket.admin_note && (
                              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-900 text-[13px] text-[#475569] dark:text-gray-300">
                                <p className="text-[11px] font-bold text-blue-600 uppercase mb-1">Support response</p>
                                {ticket.admin_note}
                              </div>
                            )}
                          </div>
                          <div className="space-y-4">
                            <h4 className="text-[13px] font-bold flex items-center gap-2">
                              <Tag className="w-4 h-4 text-blue-500" />
                              Ticket Metadata
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="p-3 bg-white dark:bg-[#262b33] rounded-lg border border-[#E2E8F0] dark:border-gray-800">
                                <p className="text-[10px] font-bold text-[#94A3B8] uppercase">Ticket ID</p>
                                <p className="text-[13px] font-mono mt-1">#SPT-{String(ticket.id).padStart(4, '0')}</p>
                              </div>
                              <div className="p-3 bg-white dark:bg-[#262b33] rounded-lg border border-[#E2E8F0] dark:border-gray-800">
                                <p className="text-[10px] font-bold text-[#94A3B8] uppercase">Created On</p>
                                <p className="text-[13px] font-medium mt-1">{formatTime(ticket.created_at)}</p>
                              </div>
                              <div className="p-3 bg-white dark:bg-[#262b33] rounded-lg border border-[#E2E8F0] dark:border-gray-800">
                                <p className="text-[10px] font-bold text-[#94A3B8] uppercase">Raised By</p>
                                <p className="text-[13px] font-medium mt-1">{ticket.user}</p>
                              </div>
                              <div className="p-3 bg-white dark:bg-[#262b33] rounded-lg border border-[#E2E8F0] dark:border-gray-800">
                                <p className="text-[10px] font-bold text-[#94A3B8] uppercase">Last Update</p>
                                <p className="text-[13px] font-medium mt-1">{formatTime(ticket.updated_at)}</p>
                              </div>
                            </div>

                            {/* Admin triage controls */}
                            {admin && (
                              <div className="flex items-center gap-2 pt-2">
                                <Select
                                  value={ticket.status}
                                  onChange={(v) => handleStatusChange(ticket, v)}
                                  disabled={updatingId === ticket.id}
                                  options={STATUS_OPTIONS}
                                  className="w-[150px]"
                                />
                                {updatingId === ticket.id && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                              </div>
                            )}

                            <button
                              onClick={() => handleDelete(ticket)}
                              disabled={deletingId === ticket.id}
                              className="flex items-center gap-1.5 text-[13px] font-medium text-red-600 hover:text-red-700 transition disabled:opacity-50"
                            >
                              {deletingId === ticket.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                              Delete ticket
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Ticket Modal */}
      <Modal
        isOpen={createOpen}
        onClose={() => !saving && setCreateOpen(false)}
        title="Create support ticket"
        maxWidth="max-w-lg"
        closeOnBackdrop
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-[12px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Title <span className="text-rose-500">*</span></label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Brief summary of the issue"
              autoFocus
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#2c313a] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={4}
              placeholder="Describe the problem in detail"
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#2c313a] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Category</label>
              <Select
                value={form.category}
                onChange={(v) => setForm((f) => ({ ...f, category: v }))}
                options={CATEGORY_OPTIONS}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Priority</label>
              <Select
                value={form.priority}
                onChange={(v) => setForm((f) => ({ ...f, priority: v }))}
                options={[
                  { value: 'HIGH', label: 'High' },
                  { value: 'MEDIUM', label: 'Medium' },
                  { value: 'LOW', label: 'Low' },
                ]}
                className="w-full"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              disabled={saving}
              className="px-4 py-2 text-[13px] font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2c313a] transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-[13px] font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:bg-blue-400 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? 'Creating…' : 'Create ticket'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Support;
